import type ChartsDataContainer from '../NetatmoCommon/ChartsDataContainer';
import {convertValueToString, getRainUnitLabel} from '../Units';
import type {Units} from '../Units';

/**
 * The annual water balance's carry-over: the closing balance (rain minus
 * evapotranspiration) of the year before the selected one, which the report's
 * cumulative curve can start from instead of 0 on 1 January.
 *
 * `metrics` is `[rain, 'ET']` at the monthly scale, keyed by the station's
 * local month starts. The history's range end is INCLUSIVE, so the sum stops
 * the second before `yearStart`: ending at `yearStart` itself would add the
 * selected year's January to the year before (the bug both reports shipped
 * with). A month without evapotranspiration counts its rain alone; a month
 * without rain is not in the history at all (the store walks the first
 * metric's months), so it is not counted.
 */
export const previousYearWaterBalance = (
  history: Pick<ChartsDataContainer, 'getChartData'>,
  stationId: number | string,
  metrics: string[],
  previousYearStart: number,
  yearStart: number,
): number => {
  const rows = history.getChartData(
    stationId.toString(),
    metrics,
    'M',
    previousYearStart,
    yearStart - 1,
  ) as Record<string, (number | null)[]>;
  return Object.values(rows).reduce(
    (balance, [rain, evapotranspiration]) =>
      balance + (rain ?? 0) - (evapotranspiration ?? 0),
    0,
  );
};

/**
 * A water balance as the report states it: signed ("+12.3 mm", "-0.118 in"),
 * at the rain unit's own precision — one decimal would read every inch figure
 * to the tenth.
 */
export const formatWaterBalance = (balance: number, units: Units): string =>
  `${balance >= 0 ? '+' : ''}${convertValueToString(
    balance,
    'Rain',
    units,
  )} ${getRainUnitLabel(units)}`;
